import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { GlobalModalActionsEnum } from '../components/document-table/DocumentTableConstants';

type ModalsState = {
  activeModal: GlobalModalActionsEnum | null;
  activeDropdown: string | null;
  showSendDocumentModal: boolean;
  showGrantAccessModal: boolean;
  showRequestInternalReviewDocumentModal: boolean;
  showSendViaEmailModal: boolean;
  showHeaderFooterEditorModal: boolean;
  showRequiredPlaceholdersModal: boolean;
  showSendForSigningPreparationModal: boolean;
  showReadyToSignModal: boolean;
  closePendingReviewWarningModal: boolean;
  setActiveModal: (modal: GlobalModalActionsEnum | null) => void;
  setActiveDropdown: (id: string | null) => void;
  setShowSendDocumentModal: (isOpen: boolean) => void;
  setShowGrantAccessModal: (isOpen: boolean) => void;
  setShowRequestInternalReviewModal: (isOpen: boolean) => void;
  setShowSendDocumentViaEmailModal: (isOpen: boolean) => void;
  setShowHeaderFooterEditorModal: (isOpen: boolean) => void;
  setShowRequiredPlaceholdersModal: (isOpen: boolean) => void;
  setShowSendForSigningPreparationModal: (isOpen: boolean) => void;
  setShowReadyToSignModal: (isOpen: boolean) => void;
  setClosePendingWarningReviewModal: (isClosed: boolean) => void;
  closeAllDropdowns: () => void;
  closeDocumentModals: () => void;
};

const ModalsContext = createContext<ModalsState | null>(null);

export const ModalsProvider = ({ children }: { children: ReactNode }) => {
  const [activeModal, setActiveModalState] = useState<GlobalModalActionsEnum | null>(null);
  const [activeDropdown, setActiveDropdownState] = useState<string | null>(null);
  const [showSendDocumentModal, setShowSendDocumentModalState] = useState(false);
  const [showGrantAccessModal, setShowGrantAccessModalState] = useState(false);
  const [showRequestInternalReviewDocumentModal, setShowRequestInternalReviewModalState] = useState(false);
  const [showSendViaEmailModal, setShowSendDocumentViaEmailModalState] = useState(false);
  const [showHeaderFooterEditorModal, setShowHeaderFooterEditorModalState] = useState(false);
  const [showRequiredPlaceholdersModal, setShowRequiredPlaceholdersModalState] = useState(false);
  const [showSendForSigningPreparationModal, setShowSendForSigningPreparationModalState] = useState(false);
  const [showReadyToSignModal, setShowReadyToSignModalState] = useState(false);
  const [closePendingReviewWarningModal, setClosePendingWarningReviewModalState] = useState(false);

  const setActiveModal = useCallback((modal: GlobalModalActionsEnum | null) => {
    setActiveModalState(modal);
  }, []);

  const setActiveDropdown = useCallback((id: string | null) => {
    setActiveDropdownState(id);
  }, []);

  const setShowSendDocumentModal = useCallback((isOpen: boolean) => {
    setShowSendDocumentModalState(isOpen);
  }, []);

  const setShowGrantAccessModal = useCallback((isOpen: boolean) => {
    setShowGrantAccessModalState(isOpen);
  }, []);

  const setShowRequestInternalReviewModal = useCallback((isOpen: boolean) => {
    setShowRequestInternalReviewModalState(isOpen);
  }, []);

  const setShowSendDocumentViaEmailModal = useCallback((isOpen: boolean) => {
    setShowSendDocumentViaEmailModalState(isOpen);
  }, []);

  const setShowHeaderFooterEditorModal = useCallback((isOpen: boolean) => {
    setShowHeaderFooterEditorModalState(isOpen);
  }, []);

  const setShowRequiredPlaceholdersModal = useCallback((isOpen: boolean) => {
    setShowRequiredPlaceholdersModalState(isOpen);
  }, []);

  const setShowSendForSigningPreparationModal = useCallback((isOpen: boolean) => {
    setShowSendForSigningPreparationModalState(isOpen);
  }, []);

  const setShowReadyToSignModal = useCallback((isOpen: boolean) => {
    setShowReadyToSignModalState(isOpen);
  }, []);

  const setClosePendingWarningReviewModal = useCallback((isClosed: boolean) => {
    setClosePendingWarningReviewModalState(isClosed);
  }, []);

  const closeAllDropdowns = useCallback(() => {
    setActiveDropdownState(null);
  }, []);

  const closeDocumentModals = useCallback(() => {
    setActiveModalState(null);
    setActiveDropdownState(null);
    setShowSendDocumentModalState(false);
    setShowGrantAccessModalState(false);
    setShowRequestInternalReviewModalState(false);
    setShowSendDocumentViaEmailModalState(false);
    setShowHeaderFooterEditorModalState(false);
    setShowRequiredPlaceholdersModalState(false);
    setShowSendForSigningPreparationModalState(false);
    setShowReadyToSignModalState(false);
    setClosePendingWarningReviewModalState(false);
  }, []);

  return (
    <ModalsContext.Provider
      value={{
        activeModal,
        activeDropdown,
        showSendDocumentModal,
        showGrantAccessModal,
        showRequestInternalReviewDocumentModal,
        showSendViaEmailModal,
        showHeaderFooterEditorModal,
        showRequiredPlaceholdersModal,
        showSendForSigningPreparationModal,
        showReadyToSignModal,
        closePendingReviewWarningModal,
        setActiveModal,
        setActiveDropdown,
        setShowSendDocumentModal,
        setShowGrantAccessModal,
        setShowRequestInternalReviewModal,
        setShowSendDocumentViaEmailModal,
        setShowHeaderFooterEditorModal,
        setShowRequiredPlaceholdersModal,
        setShowSendForSigningPreparationModal,
        setShowReadyToSignModal,
        setClosePendingWarningReviewModal,
        closeAllDropdowns,
        closeDocumentModals,
      }}
    >
      {children}
    </ModalsContext.Provider>
  );
};

export const useModals = (): ModalsState => {
  const context = useContext(ModalsContext);
  if (!context) {
    throw new Error('useModals must be used within a ModalsProvider');
  }
  return context;
};
